import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";

export default function FinanceSettlement() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [stores, setStores] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [settlementPage, setSettlementPage] = useState(1);
  const [settlementMeta, setSettlementMeta] = useState({ total: 0, total_pages: 0, last_settlement_date: null });
  const [settle, setSettle] = useState({ file: null });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [q, setQ] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [expBusy, setExpBusy] = useState("");
  const [busy, setBusy] = useState("");

  const exportSettlement = async (fmt) => {
    setExpBusy(fmt); setError("");
    try {
      const sp = storeFilter ? `&store_id=${encodeURIComponent(storeFilter)}` : "";
      const mp = platformFilter ? `&marketplace=${encodeURIComponent(platformFilter)}` : "";
      const res = await api.get(`/marketplace/settlements/export?search=${encodeURIComponent(q.trim())}${sp}${mp}&fmt=${fmt}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `settlement_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch { setError("Export settlement gagal."); } finally { setExpBusy(""); }
  };

  const load = useCallback(() => {
    api.get("/stores").then(({ data }) => setStores(data)).catch(() => {});
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (storeFilter) params.set("store_id", storeFilter);
    if (platformFilter) params.set("marketplace", platformFilter);
    params.set("page", String(settlementPage));
    params.set("page_size", "50");
    api.get(`/performance/settlements?${params.toString()}`).then(({ data }) => {
      setSettlements(Array.isArray(data?.items) ? data.items : []);
      setSettlementMeta({
        total: data?.total || 0,
        total_pages: data?.total_pages || 0,
        last_settlement_date: data?.last_settlement_date || null,
      });
    }).catch(() => {});
  }, [q, storeFilter, platformFilter, settlementPage]);

  useEffect(() => { if (isOwner) load(); }, [isOwner, load]);

  const doPreview = async () => {
    setError(""); setNotice(""); setPreview(null);
    if (!settle.file) { setError("Pilih file settlement."); return; }
    setBusy("preview");
    const fd = new FormData(); fd.append("file", settle.file);
    try {
      const { data } = await api.post("/marketplace/settlement/preview", fd);
      setPreview(data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const doCommitPartial = async () => {
    setError(""); setNotice("");
    if (!settle.file) { setError("Pilih file settlement."); return; }
    setBusy("commit");
    const fd = new FormData(); fd.append("file", settle.file);
    if (preview?.resolved?.store_id) fd.append("store_id", preview.resolved.store_id);
    try {
      const { data } = await api.post("/marketplace/settlement/commit-partial", fd);
      setNotice(`Commit READY: ${data.committed} order di-commit, ${data.skipped_duplicates || 0} duplikat dilewati, ${data.held || 0} exception tetap HOLD, ${data.excluded || 0} excluded.`);
      await doPreview(); load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const ensureBatch = async () => {
    if (preview?.import_batch_id) return preview.import_batch_id;
    const fd = new FormData(); fd.append("file", settle.file);
    if (preview?.resolved?.store_id) fd.append("store_id", preview.resolved.store_id);
    const { data } = await api.post("/marketplace/settlement/batch/ensure", fd);
    return data.import_batch_id;
  };

  const doExclude = async (orderId) => {
    setError(""); setNotice("");
    const reason = window.prompt(`Exclude order ${orderId} dari batch ini?\nRow tetap tersimpan (tidak dihapus), tercatat di AuditLog. Alasan (wajib):`);
    if (reason === null) return;
    if (!reason.trim()) { setError("Alasan exclude wajib diisi."); return; }
    setBusy(`exclude-${orderId}`);
    try {
      const batchId = await ensureBatch();
      await api.post(`/marketplace/settlement/batch/${batchId}/exclude`, { order_id: orderId, reason: reason.trim() });
      await doPreview();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const doUndoExclude = async (orderId) => {
    setError(""); setNotice("");
    if (!preview?.import_batch_id) return;
    setBusy(`undo-${orderId}`);
    try {
      await api.post(`/marketplace/settlement/batch/${preview.import_batch_id}/exclude/undo`, { order_id: orderId });
      await doPreview();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const doDownloadMissing = async () => {    setError("");
    const fd = new FormData(); fd.append("file", settle.file);
    if (preview?.resolved?.store_id) fd.append("store_id", preview.resolved.store_id);
    try {
      const res = await api.post("/marketplace/settlement/missing-receivable/export", fd, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "missing_receivable_orders.csv"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDownloadCancelRefund = async (fmt) => {
    setError("");
    const fd = new FormData(); fd.append("file", settle.file);
    if (preview?.resolved?.store_id) fd.append("store_id", preview.resolved.store_id);
    try {
      const res = await api.post(`/marketplace/settlement/cancel-refund/export?fmt=${fmt}`, fd, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const a = document.createElement("a"); a.href = window.URL.createObjectURL(blob);
      a.download = `cancel_refund_exceptions.${fmt}`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const mapShop = async (shop, storeId) => {
    setError("");
    try {
      await api.post("/mappings/store", { store_name_source: shop, store_id: storeId });
      setNotice(`Mapping tersimpan untuk '${shop}'. Memproses ulang...`);
      await doPreview();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doReclassify = async (settlementId) => {
    setError(""); setNotice("");
    try {
      const { data: cand } = await api.get(`/marketplace/settlements/${settlementId}/reclassify-candidate`);
      if (!cand.eligible) { setError("Settlement sudah diklasifikasi."); return; }
      if (!cand.candidate) { setError("Belum ada Stock-out dengan Store + Order ID yang sama untuk order ini."); return; }
      const c = cand.candidate;
      if (!window.confirm(`Reclassify ke Stock-out?\nHPP (MODAL) = ${formatIDR(c.modal)}\nPROFIT = ${formatIDR(c.profit)}\nTidak ada Wallet IN baru.`)) return;
      await api.post(`/marketplace/settlements/${settlementId}/reclassify`, { stock_out_id: c.stock_out_id });
      setNotice(`Reclassified: MODAL ${formatIDR(c.modal)}, PROFIT ${formatIDR(c.profit)}.`); load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reverseSettlement = async (s) => {
    const reason = window.prompt(`Reverse Settlement order ${s.order_id} (Net ${formatIDR(s.net)})?\nSaldo & klasifikasi dana dibalik, piutang kembali aktif. Alasan (wajib):`);
    if (reason === null) return;
    if (!reason.trim()) { setError("Alasan reversal wajib diisi."); return; }
    if (!window.confirm(`Konfirmasi: reverse settlement ${s.order_id}. Record tetap tersimpan sebagai REVERSED. Lanjut?`)) return;
    try {
      await api.post(`/marketplace/settlements/${s.id}/reverse`, { reason: reason.trim() });
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal reverse settlement."); }
  };

  if (user && !isOwner) {
    return <div className="app-shell"><AppTopBar active="accounts" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>;
  }

  const sum = preview?.summary || {};
  const isTiktok = preview?.marketplace === "TIKTOK";
  const tkExc = preview?.tiktok_exceptions || null;
  const excludedIds = new Set(preview?.excluded_order_ids || []);
  const ready = sum.ready || 0;

  // Exception rows (HOLD): MISSING_RECEIVABLE + Shopee CANCEL/REFUND + TikTok CANCEL. ADS is info-only
  // (NO Exclude action). Excluded ones move to the Excluded panel. CANCEL/REFUND stays HOLD until the
  // operator checks Ginee/ERP — never auto-excluded.
  const missingRows = (preview?.missing_pure || []).filter((r) => !excludedIds.has(r.order_id)).map((r) => ({ order_id: r.order_id, net: r.net, category: "MISSING_RECEIVABLE", detail: r.current_source_match, canExclude: true }));
  const cancelRows = (preview?.cancel_refund || []).filter((r) => !excludedIds.has(r.order_id)).map((r) => ({ order_id: r.order_id, net: r.net, category: "CANCEL_REFUND", detail: "net<0", canExclude: true }));
  const tkCancelRows = isTiktok ? (tkExc?.cancel_refund || []).filter((r) => !excludedIds.has(r.order_id)).map((r) => ({ order_id: r.order_id, net: r.net, category: "CANCEL_REFUND", detail: r.reason, canExclude: true })) : [];
  const tkAdsRows = isTiktok ? (tkExc?.ads || []).filter((r) => !excludedIds.has(r.order_id)).map((r) => ({ order_id: r.order_id, net: r.net, category: "ADS", detail: r.jenis, canExclude: false })) : [];
  const exceptionRows = [...missingRows, ...cancelRows, ...tkCancelRows, ...tkAdsRows];
  const cancelRefundCount = cancelRows.length + tkCancelRows.length;
  const excludedRows = preview?.excluded || [];

  const shownSettlements = settlements;
  const lastSettlementDate = settlementMeta.last_settlement_date || "";
  const fmtDMY = (iso) => { if (!iso) return "-"; const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; };

  const catPill = (cat) => {
    const map = { MISSING_RECEIVABLE: "warn", CANCEL_REFUND: "warn", ADS: "" };
    return <span className={`status-pill ${map[cat] || ""}`}>{cat}</span>;
  };

  return (
    <div className="app-shell">
      <AppTopBar active="accounts" />
      <main className="content">
        <div style={{ width: "100%", maxWidth: 1180, margin: "0 auto" }}>
          <Link to="/finance" className="back-link">← Dashboard Keuangan</Link>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 22 }}>
            <div><h1 style={{ margin: "0 0 6px", fontSize: 26 }}>Settlement Marketplace</h1><p className="hint" style={{ margin: 0 }}>Satu upload → review exception → Commit READY sekarang → exception tetap HOLD, selesaikan kemudian.</p></div>
            <span className="hint">OWNER · FINANCE</span>
          </div>

          {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
          {notice && <div className="alert-info" data-testid="notice-banner">{notice}</div>}

          <section className="panel panel--wide" data-testid="settlement-panel" style={{ marginTop: 0, marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
              <div><h2 className="panel-title" style={{ margin: "0 0 4px" }}>Import File Pelunasan</h2><div className="hint">Tahap 1 — pilih file dan jalankan Preview. Shopee & TikTok.</div></div>
              <span className="status-pill on">PREVIEW FIRST</span>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", padding: 14, background: "rgba(255,255,255,.45)", border: "1px solid var(--border)", borderRadius: 10 }}>
              <div style={{ minWidth: 300, flex: 1 }}><label className="field-label">File Pelunasan (.xlsx)</label><input className="field-input" type="file" accept=".xlsx" onChange={(e) => { setSettle({ file: e.target.files[0] }); setPreview(null); }} data-testid="settle-file" /></div>
              <button className="btn-primary" onClick={doPreview} disabled={!settle.file || busy === "preview"} data-testid="settle-preview-btn">{busy === "preview" ? "Memproses…" : "Preview (auto-map)"}</button>
            </div>

            {preview?.status === "SHOP_UNMAPPED" && (
              <div className="alert-error" data-testid="settle-shop-unmapped" style={{ marginTop: 12 }}>
                <strong>SHOP_UNMAPPED</strong> — Source Shop: <span className="mono">{preview.shop_source || "(kosong)"}</span> belum dipetakan.
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8, flexWrap: "wrap" }}>
                  <div><label className="field-label">Petakan ke Master Toko</label><select className="field-input" defaultValue="" id="unmapped-store-pick" data-testid="settle-map-store"><option value="">-- pilih store --</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                  <button className="btn-primary" onClick={() => { const v = document.getElementById("unmapped-store-pick").value; if (v) mapShop(preview.shop_source, v); }} data-testid="settle-map-save">Simpan Mapping & Proses Ulang</button>
                </div>
              </div>
            )}

            {preview?.resolved && (
              <div className="meta-grid" data-testid="settle-resolved" style={{ marginTop: 12, gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 8 }}>
                <div><span className="meta-label">Source Shop</span><div className="mono">{preview.resolved.shop_source || "(kosong)"}</div></div>
                <div><span className="meta-label">Master Toko</span><div className="mono">{preview.resolved.store_name}</div></div>
                <div><span className="meta-label">Wallet</span><div className="mono">{preview.resolved.account_name}</div></div>
                <div><span className="meta-label">Mapping</span><div className="mono"><span className="status-pill on">{preview.resolved.mapping}</span></div></div>
              </div>
            )}

            {preview && preview.status !== "SHOP_UNMAPPED" && preview.summary && (
              <div style={{ marginTop: 14 }} data-testid="settle-preview">
                {isTiktok && <div className="hint" style={{ marginBottom: 8 }}>Marketplace: <span className="status-pill on">TIKTOK</span></div>}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
                  {[
                    ["Ready / Matched", ready, "sum-ready"],
                    ["Missing Receivable", sum.missing_receivable || 0, "sum-missing"],
                    ["Cancel / Refund", (sum.cancel_refund || 0) + tkCancelRows.length + (isTiktok ? 0 : 0), "sum-cancel"],
                    ["Duplicate", sum.duplicate || 0, "sum-duplicate"],
                    ["Excluded", sum.excluded || 0, "sum-excluded"],
                    ["Committed", sum.committed || 0, "sum-committed"],
                    ["Net Ready", formatIDR(preview.net_total), "sum-net"],
                  ].map(([label, value, tid]) => <div key={label} data-testid={tid} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12, background: "#fff" }}><div className="meta-label">{label}</div><div className="mono" style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>{value}</div></div>)}
                </div>

                <div className="form-actions" style={{ marginTop: 14, justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <button className="btn-ghost" onClick={doPreview} disabled={busy === "preview"} data-testid="settle-refresh-btn">Refresh Unresolved</button>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {(missingRows.length > 0) && <button className="btn-ghost" onClick={doDownloadMissing} data-testid="settle-download-missing-btn">Download Missing (CSV)</button>}
                    <button className="btn-primary" onClick={doCommitPartial} disabled={ready === 0 || busy === "commit"} data-testid="settle-commit-btn">{busy === "commit" ? "Memproses…" : `Commit READY (${ready})`}</button>
                  </div>
                </div>

                {exceptionRows.length > 0 && (
                  <div style={{ marginTop: 16 }} data-testid="settle-exception-block">
                    <h3 className="panel-title" style={{ margin: "0 0 8px", fontSize: 16 }}>Exception (HOLD) — {exceptionRows.length}</h3>
                    <div className="hint" style={{ marginBottom: 8 }}>Baris ini TIDAK ikut Commit READY. CANCEL/REFUND tetap HOLD sampai operator cek Ginee/ERP (jangan auto-exclude). ADS hanya info. Selesaikan (mis. Tanam Piutang lalu Refresh) atau Exclude from Batch bila sudah dipastikan.</div>
                    {cancelRefundCount > 0 && (
                      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                        <button className="btn-mini on" onClick={() => doDownloadCancelRefund("xlsx")} data-testid="download-cancel-refund-xlsx">Download Cancel/Refund ({cancelRefundCount}) .xlsx</button>
                        <button className="btn-mini" onClick={() => doDownloadCancelRefund("csv")} data-testid="download-cancel-refund-csv">.csv</button>
                      </div>
                    )}
                    <div className="table-scroll"><table className="data-table" data-testid="exception-table"><thead><tr><th>Order ID</th><th>Kategori</th><th>Detail</th><th>Net</th><th>Aksi</th></tr></thead><tbody>
                      {exceptionRows.map((r) => <tr key={`${r.category}-${r.order_id}`} data-testid={`exception-row-${r.order_id}`}>
                        <td className="mono">{r.order_id}</td>
                        <td>{catPill(r.category)}</td>
                        <td className="mono">{r.detail || "-"}</td>
                        <td className="mono">{formatIDR(r.net)}</td>
                        <td>{r.canExclude
                      ? <button className="btn-mini" onClick={() => doExclude(r.order_id)} disabled={busy === `exclude-${r.order_id}`} data-testid={`exclude-btn-${r.order_id}`}>Exclude from Batch</button>
                      : <span className="hint" data-testid={`ads-noexclude-${r.order_id}`}>Info only (ADS)</span>}</td>
                      </tr>)}
                    </tbody></table></div>
                  </div>
                )}

                {excludedRows.length > 0 && (
                  <div style={{ marginTop: 16 }} data-testid="settle-excluded-block">
                    <h3 className="panel-title" style={{ margin: "0 0 8px", fontSize: 16 }}>Excluded from Batch — {excludedRows.length}</h3>
                    <div className="hint" style={{ marginBottom: 8 }}>Persisten per batch (bukan exclude global). Tercatat di AuditLog.</div>
                    <div className="table-scroll"><table className="data-table" data-testid="excluded-table"><thead><tr><th>Order ID</th><th>Alasan</th><th>Aksi</th></tr></thead><tbody>
                      {excludedRows.map((r) => <tr key={r.order_id} data-testid={`excluded-row-${r.order_id}`}>
                        <td className="mono">{r.order_id}</td>
                        <td>{r.reason || "-"}</td>
                        <td><button className="btn-mini" onClick={() => doUndoExclude(r.order_id)} disabled={busy === `undo-${r.order_id}`} data-testid={`undo-exclude-btn-${r.order_id}`}>Undo Exclude</button></td>
                      </tr>)}
                    </tbody></table></div>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="panel panel--wide" style={{ marginTop: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <h2 className="panel-title" style={{ margin: 0 }}>Riwayat Settlement</h2>
                <select className="field-input" style={{ width: "auto", minWidth: 150, padding: "4px 8px" }} value={storeFilter} onChange={(e) => { setStoreFilter(e.target.value); setSettlementPage(1); }} data-testid="settlement-store-filter">
                  <option value="">Semua Toko</option>
                  {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className="field-input" style={{ width: "auto", minWidth: 140, padding: "4px 8px" }} value={platformFilter} onChange={(e) => { setPlatformFilter(e.target.value); setSettlementPage(1); }} data-testid="settlement-platform-filter">
                  <option value="">Semua Platform</option>
                  <option value="SHOPEE">SHOPEE</option>
                  <option value="TIKTOK">TIKTOK</option>
                </select>
                <span className="hint" data-testid="settlement-last-date">Settlement terakhir: {fmtDMY(lastSettlementDate)}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ width: 260, maxWidth: "100%" }}><input className="field-input" value={q} onChange={(e) => { setQ(e.target.value); setSettlementPage(1); }} placeholder="Cari Order ID / Invoice..." data-testid="settlement-search" /></div>
                <button className="btn-mini on" disabled={!!expBusy} onClick={() => exportSettlement("xlsx")} data-testid="export-settlement-xlsx">{expBusy === "xlsx" ? "Menyiapkan…" : "Export Settlement (.xlsx)"}</button>
                <button className="btn-mini" disabled={!!expBusy} onClick={() => exportSettlement("csv")} data-testid="export-settlement-csv">.csv</button>
                <span className="hint">{settlementMeta.total} transaksi</span>
              </div>
            </div>
            <div className="table-scroll"><table className="data-table data-table--settlement" data-testid="settlements-table"><thead><tr><th>Store</th><th>Order ID</th><th>Sumber Match</th><th>Status</th><th>Tanggal</th><th>Gross</th><th>Fee</th><th>Net</th><th>HPP</th><th>Modal</th><th>Profit</th><th>Aksi</th></tr></thead><tbody>
              {shownSettlements.length === 0 && <tr><td colSpan={12} className="empty-cell">Belum ada settlement.</td></tr>}
              {shownSettlements.map((s) => <tr key={s.id} data-testid={`settlement-row-${s.order_id}`}><td className="mono">{s.store_name}</td><td className="mono">{s.order_id}</td><td>{s.match_source ? <span className="status-pill on">{s.match_source}</span> : "-"}</td><td>{s.status === "PENDING_CLASSIFICATION" ? <span className="status-pill">PENDING</span> : s.status === "REVERSED" ? <span className="status-pill warn">REVERSED</span> : <span className="status-pill on">CLASSIFIED</span>}</td><td>{s.settlement_date || "-"}</td><td className="mono">{formatIDR(s.gross)}</td><td className="mono">{formatIDR(s.total_deduction)}</td><td className="mono">{formatIDR(s.net)}</td><td className="mono">{formatIDR(s.hpp)}</td><td className="mono">{formatIDR(s.modal)}</td><td className="mono">{formatIDR(s.profit)}</td><td>{s.status === "PENDING_CLASSIFICATION" ? <button className="btn-mini" onClick={() => doReclassify(s.id)}>Reclassify</button> : (s.status === "CLASSIFIED" && s.match_source === "RECEIVABLE") ? <button className="btn-mini" onClick={() => reverseSettlement(s)}>Reverse</button> : "-"}</td></tr>)}
            </tbody></table></div>
            {settlementMeta.total_pages > 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 10 }} data-testid="settlement-pagination">
                <button className="btn-mini" disabled={settlementPage <= 1} onClick={() => setSettlementPage((p) => Math.max(1, p - 1))}>← Sebelumnya</button>
                <span className="hint">Halaman {settlementPage} / {settlementMeta.total_pages}</span>
                <button className="btn-mini" disabled={settlementPage >= settlementMeta.total_pages} onClick={() => setSettlementPage((p) => p + 1)}>Berikutnya →</button>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
